import {
  inventoryItemIdFormat,
  locationIdFormat,
  packingTemplateIdFormat,
  productIdFormat,
  storageLocationIdFormat,
  transferSessionIdFormat,
} from "@redotech/redo-ids/ids";
import { packingMode } from "@redotech/redo-model/inventory/packing-mode";
import * as z from "zod";

// One line in a transfer's bin maps: product-backed inventory keys by
// ProductId (units of the same product aggregate); license-plated units
// without a product key by their InventoryItemId (ii_…), one line per unit.
const TransferLineKeySchema_ = z.union([
  productIdFormat.schema,
  inventoryItemIdFormat.schema,
]);
export type TransferLineKey = z.infer<typeof TransferLineKeySchema_>;

// Input schemas
// quantity: new items to add (creates new adjustment if > 0)
// executed: quantity to execute from existing adjustment
const TransferItemDestinationSchema_ = z.object({
  quantity: z.number().int().nonnegative().max(1_000_000).optional(),
  executed: z.number().int().nonnegative().max(1_000_000).optional(),
});
export interface TransferItemDestination extends z.infer<
  typeof TransferItemDestinationSchema_
> {}

const TransferItemInputSchema_ = z.object({
  // Set when this line is a license-plated unit rather than a product
  // aggregate. The line's map key is the same id; quantity is always 1.
  inventoryItemId: inventoryItemIdFormat.schema.optional(),
  quantity: z.number().int().nonnegative().max(1_000_000).optional(),
  executed: z.number().int().nonnegative().max(1_000_000).optional(),
  lost: z.number().int().nonnegative().max(1_000_000).optional(),
  packingMode: z.enum(packingMode).optional(),
  packingTemplateId: packingTemplateIdFormat.schema.nullable().optional(),
  enteredQuantity: z.number().int().nonnegative().optional(),
  destinations: z
    .record(storageLocationIdFormat.schema, TransferItemDestinationSchema_)
    .optional(),
  // Operator-reported final quantity in the destination bin AFTER the items
  // being received have been placed. When set on a destination item, the
  // server treats this as a one-shot onboarding count: if the bin's
  // inventory_level is uncounted (last_counted_at IS NULL), the level is
  // reconciled so the post-receive total matches this value via a bin_count
  // adjustment against the Lost bin, and last_counted_at is set to NOW(),
  // taking the bin out of uncalibrated state. If the bin is already counted,
  // this field is ignored (race safety).
  onboardingFinalQuantity: z
    .number()
    .int()
    .nonnegative()
    .max(1_000_000)
    .optional(),
});
export interface TransferItemInput extends z.infer<
  typeof TransferItemInputSchema_
> {}

const StorageLocationMapInputSchema_ = z.record(
  storageLocationIdFormat.schema,
  z.record(TransferLineKeySchema_, TransferItemInputSchema_),
);
export type StorageLocationMapInput = z.infer<
  typeof StorageLocationMapInputSchema_
>;

// Output schemas
const TransferItemStateSchema_ = z.object({
  // Set when this line is a license-plated unit keyed by its inventory item
  // id; display info lives in TransferSessionState.inventoryItemInfo.
  inventoryItemId: inventoryItemIdFormat.schema.nullish(),
  quantity: z.number(),
  committed: z.number(),
  executed: z.number(),
  lost: z.number(),
  // slta.position — ascending across both sourceing and distributing phases.
  // For source items this is scan order. For destination items this mirrors
  // the source scan order of the same product (see addDestinationsToTransfer).
  // Clients that want cross-bin scan-order walking flatten destination[bin]
  // values and sort by this field.
  position: z.number().nullish(),
  // slta.skip_index — set when the operator skips a (bin, product) cell
  // during the distributing phase. Null = not skipped. Non-null values sort
  // after non-skipped items (ascending), so re-skipping a cell pushes it
  // further back. Mirrors the pick-session skipIndex convention.
  skipIndex: z.number().nullish(),
});
export interface TransferItemState extends z.infer<
  typeof TransferItemStateSchema_
> {}

const StorageLocationMapStateSchema_ = z.record(
  storageLocationIdFormat.schema,
  z.record(TransferLineKeySchema_, TransferItemStateSchema_),
);
export type StorageLocationMapState = z.infer<
  typeof StorageLocationMapStateSchema_
>;

const TransferErrorSchema_ = z.object({
  storageLocationId: storageLocationIdFormat.schema,
  productId: TransferLineKeySchema_,
  message: z.string(),
});
export interface TransferError extends z.infer<typeof TransferErrorSchema_> {}

export const TransferSessionStatusSchema = z.enum([
  "sourcing",
  "distributing",
  "completed",
]);
export type TransferSessionStatus = z.infer<typeof TransferSessionStatusSchema>;

const StorageLocationInfoSchema_ = z.object({
  name: z.string(),
  type: z.string(),
});
export interface StorageLocationInfo extends z.infer<
  typeof StorageLocationInfoSchema_
> {}

const ProductInfoSchema_ = z.object({
  title: z.string(),
  sku: z.string().nullable(),
  imageUrl: z.string().nullable(),
  barcode: z.string().nullable(),
  barcodes: z.array(z.string()),
  caseBarcodes: z.array(z.string()).optional().default([]),
});
export interface ProductInfo extends z.infer<typeof ProductInfoSchema_> {}

const InventoryItemInfoSchema_ = z.object({
  kind: z.string().nullable(),
  grade: z.string().nullable(),
  product: z
    .object({
      title: z.string(),
      sku: z.string().nullable(),
      imageUrl: z.string().nullable(),
    })
    .nullable(),
});
export interface InventoryItemInfo extends z.infer<
  typeof InventoryItemInfoSchema_
> {}

const DestinationItemMetaSchema_ = z.object({
  currentQuantity: z.number(),
  maxQuantity: z.number().nullable(),
});
export interface DestinationItemMeta extends z.infer<
  typeof DestinationItemMetaSchema_
> {}

const DestinationMetaSchema_ = z.record(
  storageLocationIdFormat.schema,
  z.record(TransferLineKeySchema_, DestinationItemMetaSchema_),
);
export type DestinationMeta = z.infer<typeof DestinationMetaSchema_>;

const TransferSessionStateSchema_ = z.object({
  transferId: transferSessionIdFormat.schema,
  status: TransferSessionStatusSchema,
  sourceLocationId: locationIdFormat.schema.nullish(),
  destinationLocationId: locationIdFormat.schema.nullish(),
  source: StorageLocationMapStateSchema_,
  sourceOrder: z.array(storageLocationIdFormat.schema),
  destination: StorageLocationMapStateSchema_.optional(),
  destinationOrder: z.array(storageLocationIdFormat.schema).optional(),
  // Storage location metadata (id -> name, type) for display and unmapped detection
  storageLocationInfo: z
    .record(storageLocationIdFormat.schema, StorageLocationInfoSchema_)
    .optional(),
  // Product metadata (id -> title, sku, imageUrl) for display
  productInfo: z.record(productIdFormat.schema, ProductInfoSchema_).optional(),
  // License-plated unit metadata (inventory item id -> kind, grade, product)
  // for display of item-keyed lines
  inventoryItemInfo: z
    .record(inventoryItemIdFormat.schema, InventoryItemInfoSchema_)
    .optional(),
  // Per-destination bin+product metadata (max_quantity, current quantity) for replenishment
  destinationMeta: DestinationMetaSchema_.optional(),
  errors: z.array(TransferErrorSchema_),
});
export const TransferSessionStateSchema: z.ZodType<TransferSessionState> =
  TransferSessionStateSchema_;
export interface TransferSessionState extends z.infer<
  typeof TransferSessionStateSchema_
> {}

// POST - Create session with initial sources

const ReceivingItemSchema_ = z.object({
  productId: productIdFormat.schema,
  quantity: z.number().int().positive(),
});

const TransferVariantSchema_ = z
  .object({
    locationId: locationIdFormat.schema,
    destinationLocationId: locationIdFormat.schema.nullish(),
    type: z.literal("transfer"),
    source: StorageLocationMapInputSchema_,
    sourceOrder: z.array(storageLocationIdFormat.schema),
    referenceId: z.string().max(500).optional(),
  })
  .strict();

const ReceivingVariantSchema_ = z
  .object({
    locationId: locationIdFormat.schema,
    type: z.literal("receiving"),
    items: z.array(ReceivingItemSchema_).min(1),
    referenceId: z.string().max(500).optional(),
    // Set for receive-and-place; leave unset to park items in RECEIVING.
    ephemeralTransit: z.boolean().optional(),
  })
  .strict();

const LostItemsVariantSchema_ = z
  .object({
    locationId: locationIdFormat.schema,
    type: z.literal("lost"),
    items: z.array(ReceivingItemSchema_).min(1),
    referenceId: z.string().max(500).optional(),
  })
  .strict();

const BackwardCompatTransferSchema_ = z
  .object({
    locationId: locationIdFormat.schema,
    destinationLocationId: locationIdFormat.schema.nullish(),
    source: StorageLocationMapInputSchema_,
    sourceOrder: z.array(storageLocationIdFormat.schema),
    referenceId: z.string().max(500).optional(),
  })
  .strict();

const CreateTransferSessionInputSchema_ = z.union([
  z.discriminatedUnion("type", [
    TransferVariantSchema_,
    ReceivingVariantSchema_,
    LostItemsVariantSchema_,
  ]),
  BackwardCompatTransferSchema_,
]);

export const createTransferSession = {
  name: "createTransferSession" as const,
  input: CreateTransferSessionInputSchema_,
  output: TransferSessionStateSchema,
};

export type CreateTransferSessionInput = z.infer<
  typeof CreateTransferSessionInputSchema_
>;

// PATCH - Add sources OR add destinations, optionally complete phase
export const updateTransferSession = {
  name: "updateTransferSession" as const,
  input: z.object({
    transferId: transferSessionIdFormat.schema,
    // Sourcing phase - add more sources
    source: StorageLocationMapInputSchema_.optional(),
    sourceOrder: z.array(storageLocationIdFormat.schema).optional(),
    // Distribution phase - add destinations
    destination: StorageLocationMapInputSchema_.optional(),
    destinationOrder: z.array(storageLocationIdFormat.schema).optional(),
    // Set/change the destination location for cross-warehouse transfers
    destinationLocationId: locationIdFormat.schema.nullish(),
    // Explicit completion: "distributing" completes sourcing, "completed" completes distributing
    state: z.enum(["distributing", "completed"]).optional(),
  }),
  output: TransferSessionStateSchema,
};

export type UpdateTransferSessionInput = z.infer<
  typeof updateTransferSession.input
>;

// GET - Retrieve current session state
export const getTransferSessionState = {
  name: "getTransferSessionState" as const,
  input: z.object({ transferId: transferSessionIdFormat.schema }),
  output: TransferSessionStateSchema,
};

export type GetTransferSessionStateInput = z.infer<
  typeof getTransferSessionState.input
>;

// PATCH - Skip a (bin, product) cell so it sorts to the back of the
// distributing walk. Bumps slta.skip_index to MAX + 1.
export const skipTransferSessionItem = {
  name: "skipTransferSessionItem" as const,
  input: z.object({
    transferId: transferSessionIdFormat.schema,
    storageLocationId: storageLocationIdFormat.schema,
    productId: TransferLineKeySchema_,
  }),
  output: TransferSessionStateSchema,
};

export type SkipTransferSessionItemInput = z.infer<
  typeof skipTransferSessionItem.input
>;

export { type TransferSessionId } from "@redotech/redo-ids/ids";
